/**
 * @file Sidebar.tsx
 * @author Arunkumar Annamalai
 * @version 1.0.0
 * @description Sidebar navigation component for the Shay Suite Admin application.
 *              This file provides the main navigation menu with role-based access control,
 *              responsive design, user profile management, and theme-aware styling.
 *              It supports both expanded and collapsed states with smooth transitions.
 * @created Aug-2025
 * @lastModified Aug-2025
 */

// src/components/Sidebar.tsx

import React, { useState } from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  styled,
  Menu,
  MenuItem,
  Divider,
  Avatar,
  Chip,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import ChatIcon from '@mui/icons-material/Chat';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';

import AccountCircle from '@mui/icons-material/AccountCircle';
import BusinessIcon from '@mui/icons-material/Business';
import EmailIcon from '@mui/icons-material/Email';
import LogoutIcon from '@mui/icons-material/Logout';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { APP_CONFIG } from '../helpers/constants';

// Sidebar configuration constants
const drawerWidth = 240;
const closedDrawerWidth = 60; // Width when collapsed

/**
 * Props interface for the Sidebar component
 */
interface SidebarProps {
  open: boolean;                    // Whether the sidebar is expanded
  handleDrawerClose: () => void;    // Function to close the sidebar
  handleDrawerOpen: () => void;     // Function to open the sidebar
  onLogout: () => void;             // Function to handle user logout
}

/**
 * Styled component for the drawer header
 * Provides consistent styling for the logo and title area
 */
const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(0, 1),
  // necessary for content to be below app bar
  ...theme.mixins.toolbar,
  justifyContent: 'flex-start',
}));

/**
 * Sidebar navigation component with role-based menu items
 * 
 * This component:
 * - Provides navigation links with role-based access control
 * - Supports responsive design with expand/collapse functionality
 * - Includes user profile management with dropdown menu
 * - Handles theme-aware styling and logo switching
 * - Manages active state highlighting for current route
 * 
 * @param {SidebarProps} props - Component props for sidebar behavior
 * @returns {JSX.Element} The complete sidebar navigation structure
 */
const Sidebar: React.FC<SidebarProps> = ({ open, handleDrawerClose: _handleDrawerClose, handleDrawerOpen: _handleDrawerOpen, onLogout }) => {
  const { isDarkMode } = useTheme();
  const { isRootAdmin, user } = useAuth();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  
  // If user is not a root admin, don't render the sidebar
  if (!isRootAdmin) {
    return null;
  }
  
  // Theme-aware logo paths (same as shay-suite-frontend: full logo when open, icon when closed)
  const fullLogoSrc = isDarkMode ? '/fulllogo-white.png' : '/fulllogo.png';
  const logoIconSrc = isDarkMode ? '/logo-white.png' : '/logo.png';
  
  // Active state tracking for navigation items
  const isDashboardActive = location.pathname === '/dashboard' || location.pathname === '/';
  const isCustomersActive = location.pathname === '/customers';
  const isSubscriptionPlansActive = location.pathname === '/subscription';
  const isApiKeysActive = location.pathname === '/apiKeys';
  const isChatServiceActive = location.pathname === '/chatService';
  const isAdminHubActive = location.pathname === '/adminHub';

  /**
   * Opens the profile menu dropdown
   * 
   * @param {React.MouseEvent<HTMLElement>} event - The click event that triggered the menu
   */
  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  /**
   * Closes the profile menu dropdown
   */
  const handleClose = () => {
    setAnchorEl(null);
  };

  /**
   * Handles user logout action
   * Closes the menu and triggers the logout function
   */
  const handleLogout = () => {
    handleClose();
    onLogout();
  };

  // Get company name or fallback to user email, truncate if too long
  // Display name for collapsed sidebar: name, company, or email local part
  const fullCompanyName = user?.name || user?.companyName || user?.email?.split('@')[0] || 'Profile';
  const displayName = fullCompanyName.length > 15 ? fullCompanyName.substring(0, 15) + '...' : fullCompanyName;

  /**
   * Get user's first letter for avatar (name > email > 'U')
   * Matches reference: sidebar and profile menu show user initial
   */
  const getInitials = () => {
    const name = user?.name?.trim();
    const email = user?.email?.trim();
    if (name && name.length > 0) return name.charAt(0).toUpperCase();
    if (email && email.length > 0) return email.charAt(0).toUpperCase();
    return 'U';
  };

  return (
    <Drawer
      variant="permanent"
      sx={{
        width: open ? drawerWidth : closedDrawerWidth,
        flexShrink: 0,
        whiteSpace: 'nowrap',
        boxSizing: 'border-box',
        transition: (theme) =>
          theme.transitions.create('width', {
            easing: theme.transitions.easing.sharp,
            duration: open ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
          }),
        '& .MuiDrawer-paper': {
          width: open ? drawerWidth : closedDrawerWidth,
          overflowX: 'hidden',
          backgroundColor: isDarkMode ? 'background.paper' : 'primary.main',
          color: isDarkMode ? 'text.primary' : 'white',
          transition: (theme) =>
            theme.transitions.create('width', {
              easing: theme.transitions.easing.sharp,
              duration: open ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
            }),
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DrawerHeader sx={{ 
        backgroundColor: isDarkMode ? 'background.paper' : 'white',
        color: 'text.primary',
        justifyContent: 'flex-start',
        pl: 2
      }}>
        {open ? (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%' }}>
            <Box
              component="img"
              src={fullLogoSrc}
              alt={APP_CONFIG.NAME}
              sx={{
                height: 30,
                width: 'auto',
                maxWidth: '100%',
                objectFit: 'contain',
              }}
            />
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', width: '100%' }}>
            <Box
              component="img"
              src={logoIconSrc}
              alt={APP_CONFIG.NAME}
              sx={{
                height: 24,
                width: 'auto',
                maxWidth: '100%',
                objectFit: 'contain',
              }}
            />
          </Box>
        )}
      </DrawerHeader>
      
      <List sx={{ flexGrow: 1 }}>
        {/* Dashboard link - visible to all */}
        <ListItem disablePadding sx={{ display: 'none' }}>
          <ListItemButton
            component={RouterLink}
            to="/dashboard"
            selected={isDashboardActive}
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              px: 2.5,
              color: isDarkMode ? 'text.primary' : 'white',
              '&:hover': {
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.08)',
              },
              '&.Mui-selected': {
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.12)',
                color: isDarkMode ? 'text.primary' : 'white',
                '&:hover': {
                  backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.16)' : 'rgba(255, 255, 255, 0.16)',
                },
              },
            }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : 'auto',
                justifyContent: 'center',
                color: isDarkMode ? 'text.primary' : 'white',
              }}
            >
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText primary="Dashboard" sx={{ opacity: open ? 1 : 0 }} />
          </ListItemButton>
        </ListItem>

        {/* Root Admin Only Menu Items */}
        {isRootAdmin && (
          <>
            {/* Customers link */}
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                component={RouterLink}
                to="/customers"
                selected={isCustomersActive}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  color: isDarkMode ? 'text.primary' : 'white',
                  '&:hover': {
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.08)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.12)',
                    color: isDarkMode ? 'text.primary' : 'white',
                    '&:hover': {
                      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.16)' : 'rgba(255, 255, 255, 0.16)',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                    color: isDarkMode ? 'text.primary' : 'white',
                  }}
                >
                  <PeopleIcon />
                </ListItemIcon>
                <ListItemText primary="Customers" sx={{ opacity: open ? 1 : 0 }} />
              </ListItemButton>
            </ListItem>

            {/* Subscription Plans link */}
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                component={RouterLink}
                to="/subscription"
                selected={isSubscriptionPlansActive}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  color: isDarkMode ? 'text.primary' : 'white',
                  '&:hover': {
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.08)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.12)',
                    color: isDarkMode ? 'text.primary' : 'white',
                    '&:hover': {
                      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.16)' : 'rgba(255, 255, 255, 0.16)',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                    color: isDarkMode ? 'text.primary' : 'white',
                  }}
                >
                  <CreditCardIcon />
                </ListItemIcon>
                <ListItemText primary="Subscription Plans" sx={{ opacity: open ? 1 : 0 }} />
              </ListItemButton>
            </ListItem>

            {/* API Keys link */}
            <ListItem disablePadding sx={{ display: 'none' }}>
              <ListItemButton
                component={RouterLink}
                to="/apiKeys"
                selected={isApiKeysActive}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  color: isDarkMode ? 'text.primary' : 'white',
                  '&:hover': {
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.08)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.12)',
                    color: isDarkMode ? 'text.primary' : 'white',
                    '&:hover': {
                      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.16)' : 'rgba(255, 255, 255, 0.16)',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                    color: isDarkMode ? 'text.primary' : 'white',
                  }}
                >
                  <VpnKeyIcon />
                </ListItemIcon>
                <ListItemText primary="API Keys" sx={{ opacity: open ? 1 : 0 }} />
              </ListItemButton>
            </ListItem>

            {/* Chat Service link */}
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                component={RouterLink}
                to="/chatService"
                selected={isChatServiceActive}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  color: isDarkMode ? 'text.primary' : 'white',
                  '&:hover': {
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.08)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.12)',
                    color: isDarkMode ? 'text.primary' : 'white',
                    '&:hover': {
                      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.16)' : 'rgba(255, 255, 255, 0.16)',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                    color: isDarkMode ? 'text.primary' : 'white',
                  }}
                >
                  <ChatIcon />
                </ListItemIcon>
                <ListItemText primary="Chat Service" sx={{ opacity: open ? 1 : 0 }} />
              </ListItemButton>
            </ListItem>

            {/* Admin Hub link */}
            <ListItem disablePadding sx={{ display: 'block' }}>
              <ListItemButton
                component={RouterLink}
                to="/adminHub"
                selected={isAdminHubActive}
                sx={{
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                  px: 2.5,
                  color: isDarkMode ? 'text.primary' : 'white',
                  '&:hover': {
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.08)',
                  },
                  '&.Mui-selected': {
                    backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.12)',
                    color: isDarkMode ? 'text.primary' : 'white',
                    '&:hover': {
                      backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.16)' : 'rgba(255, 255, 255, 0.16)',
                    },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                    color: isDarkMode ? 'text.primary' : 'white',
                  }}
                >
                  <AdminPanelSettingsIcon />
                </ListItemIcon>
                <ListItemText primary="Admin Hub" sx={{ opacity: open ? 1 : 0 }} />
              </ListItemButton>
            </ListItem>
          </>
        )}

      </List>

      {/* Profile Menu at Bottom */}
      <Box sx={{ 
        borderTop: `1px solid ${isDarkMode ? 'rgba(255, 255, 255, 0.12)' : 'rgba(255, 255, 255, 0.12)'}`,
        p: 1
      }}>
        <ListItem disablePadding sx={{ display: 'block' }}>
          <ListItemButton
            onClick={handleMenu}
            sx={{
              minHeight: 48,
              justifyContent: open ? 'initial' : 'center',
              px: 2.5,
              color: isDarkMode ? 'text.primary' : 'white',
              '&:hover': {
                backgroundColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.08)',
              },
            }}
          >
            {/* Avatar with user's first letter (reference: sidebar shows initial) */}
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: open ? 3 : 'auto',
                justifyContent: 'center',
                color: isDarkMode ? 'text.primary' : 'white',
              }}
            >
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  bgcolor: isDarkMode ? 'primary.main' : 'white',
                  color: isDarkMode ? 'white' : 'primary.main',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                }}
              >
                {getInitials()}
              </Avatar>
            </ListItemIcon>
            <ListItemText 
              primary={displayName} 
              sx={{ 
                opacity: open ? 1 : 0,
                '& .MuiListItemText-primary': {
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }
              }} 
            />
          </ListItemButton>
        </ListItem>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleClose}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
          }}
          PaperProps={{
            sx: {
              minWidth: 280,
              mt: 1,
              boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
              borderRadius: 2,
            }
          }}
        >
          {/* Profile Card Header: name, email, company (order matches reference image) */}
          <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
              {/* Avatar with user's first letter (reference: profile menu shows initial) */}
              <Avatar sx={{ mr: 2, bgcolor: 'primary.main', mt: 0.5, fontSize: '1.1rem', fontWeight: 600 }}>
                {getInitials()}
              </Avatar>
              <Box sx={{ flexGrow: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                  {user?.name || user?.companyName || user?.email?.split('@')[0] || 'User'}
                </Typography>
                {/* Email with envelope icon (reference: name → email → company order) */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <EmailIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {user?.email || 'No email'}
                  </Typography>
                </Box>
                {/* Company name with building icon */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                  <BusinessIcon sx={{ mr: 1, fontSize: 16, color: 'text.secondary' }} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {user?.companyName || '—'}
                  </Typography>
                </Box>
                <Chip
                  label={isRootAdmin ? 'Root Administrator' : 'Admin'}
                  size="small"
                  sx={{
                    height: 20,
                    fontSize: '0.7rem',
                    fontWeight: 500,
                    bgcolor: isRootAdmin ? 'primary.50' : 'success.50',
                    color: isRootAdmin ? 'primary.700' : 'success.700',
                    border: '1px solid',
                    borderColor: isRootAdmin ? 'primary.200' : 'success.200',
                    '& .MuiChip-label': {
                      px: 1,
                    }
                  }}
                />
              </Box>
            </Box>
          </Box>
          
          <Divider />
          
          <MenuItem onClick={handleClose} sx={{ py: 1.5 }}>
            <AccountCircle sx={{ mr: 2, fontSize: 20 }} />
            Profile Settings
          </MenuItem>
          
          <Divider />
          
          <MenuItem onClick={handleLogout} sx={{ py: 1.5, color: 'error.main' }}>
            <LogoutIcon sx={{ mr: 2, fontSize: 20 }} />
            Logout
          </MenuItem>
        </Menu>
      </Box>
    </Drawer>
  );
};

export default Sidebar;